import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Loader2, LogOut, Check } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const input = "w-full h-10 px-3 bg-bt-surface-2 border border-bt-border-subtle text-bt-text-primary font-mono text-sm focus:outline-none focus:border-bt-gold";

const Rule = ({ ok, children }) => (
  <li className={`flex items-center gap-1.5 ${ok ? "text-bt-positive" : "text-bt-text-tertiary"}`}>
    <Check className={`w-3 h-3 ${ok ? "opacity-100" : "opacity-30"}`} /> {children}
  </li>
);

export default function ForcePasswordChange() {
  const { user, logout, clearMustChangePassword } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const longEnough = pw.length >= 8;
  const matches = pw.length > 0 && pw === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!longEnough || !matches) return;
    setSaving(true);
    setError("");
    try {
      await axios.put(`${BACKEND}/api/auth/temp-password`, { new_password: pw }, { withCredentials: true });
      toast.success("Password updated. Welcome aboard!");
      clearMustChangePassword();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not update password");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[300] bg-bt-bg/95 backdrop-blur-md flex items-center justify-center p-4" data-testid="force-password-change">
      <form onSubmit={submit} className="w-full max-w-md bg-bt-surface border border-bt-gold/40 shadow-2xl p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 flex items-center justify-center bg-bt-gold/15 border border-bt-gold/40">
            <KeyRound className="w-5 h-5 text-bt-gold" />
          </div>
          <div>
            <h1 className="font-heading text-2xl text-bt-text-primary leading-tight">Set your new password</h1>
            <p className="text-xs font-mono text-bt-text-tertiary">{user?.email}</p>
          </div>
        </div>
        <p className="text-sm text-bt-text-secondary mb-5">
          You signed in with a temporary password. Choose your own password to continue.
        </p>
        <label className="block text-[11px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">New password</label>
        <input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} className={`${input} mb-3`}
          autoComplete="new-password" data-testid="force-pw-new" />
        <label className="block text-[11px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">Confirm new password</label>
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={`${input} mb-3`}
          autoComplete="new-password" data-testid="force-pw-confirm" />
        <ul className="text-[11px] font-mono space-y-1 mb-4" data-testid="force-pw-rules">
          <Rule ok={longEnough}>At least 8 characters</Rule>
          <Rule ok={matches}>Passwords match</Rule>
          <Rule ok={longEnough}>Different from your temporary password</Rule>
        </ul>
        {error && <p className="text-xs text-bt-negative mb-3" data-testid="force-pw-error">{error}</p>}
        <button type="submit" disabled={saving || !longEnough || !matches} data-testid="force-pw-submit"
          className="w-full h-10 flex items-center justify-center gap-2 bg-bt-gold text-bt-bg font-mono text-sm font-semibold hover:bg-bt-gold/85 disabled:opacity-40 transition-colors">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save and continue
        </button>
        <button type="button" onClick={logout} data-testid="force-pw-logout"
          className="w-full mt-3 flex items-center justify-center gap-1.5 text-xs font-mono text-bt-text-tertiary hover:text-bt-text-primary">
          <LogOut className="w-3.5 h-3.5" /> Log out
        </button>
      </form>
    </div>
  );
}
